/**
 * Email-Automatisierungen — gemeinsames Entity-Template.
 *
 * Kopfband (Zurueck · Breadcrumb · dt. H1 · KPI-Chips · Aktionsleiste) + Body.
 * Von ALLEN Detailseiten geteilt — den Hierarchie-Drills (Prozess/Task/Hebel/
 * Roadmap in EmailAutomationHierarchy) UND den Entscheidungs-Detailseiten
 * (Quick-Win/Prereq/Familie/Agent-Stufe in EmailAutomationDecide). In eine
 * eigene Datei extrahiert, damit beide Seiten dasselbe Template nutzen, ohne
 * einen Import-Zyklus zwischen den beiden Modulen zu erzeugen.
 */
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import clsx from "clsx";

// Tastatur-Bedienbarkeit fuer klickbare <div>/<Card>-Zeilen & -Kacheln (Befund 4,
// AK 6): role=button + tabIndex + Enter/Space. Der Fokusring kommt aus index.css
// (.ea-tab [role="button"]:focus-visible). Neben dem bestehenden onClick spreaden.
export function keyActivate(onActivate: () => void) {
  return {
    role: "button" as const,
    tabIndex: 0,
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(); }
    },
  };
}

// doc_typ → deutsches Label (sichtbar; nie ein Roh-Key als Titel/Zelle).
export const DOC_TYP_LABEL: Record<string, string> = {
  coa: "CoA (Analysenzertifikat)",
  delivery_note: "Lieferschein",
  customs_export: "Zoll-/Exportdokumente",
  invoice: "Rechnung",
  order_confirmation: "Auftragsbestätigung",
  purchase_order: "Bestellung (PO)",
  po_forwarded: "PO-Weiterleitung",
  price_date_confirm: "Preis-/Termin-Bestätigung",
  po_correction: "PO-Korrektur",
  batch_record: "Batch-Dokumentation",
  batch_release: "Batch-Freigabe",
  spec_approval: "Spezifikations-Freigabe",
  review_sign: "Review & Signatur",
  regulatory_compliance: "Regulatorik/Compliance",
  packing_list_pallet: "Packliste/Paletten",
  shipping_pickup: "Abholung/Spedition",
};
export function docLabel(t: string): string { return DOC_TYP_LABEL[t] ?? t.replace(/_/g, " "); }

// Akteur-/Routing-Codes (akteur_von_an) → Klartext, damit auf L1/L2 keine
// Roh-Tokens wie „bitop_scm → customer" stehen (Jargon-Layer, Befund 6).
const AKTEUR_MAP: Record<string, string> = {
  bitop_scm: "SCM", bitop_ef: "EF", bitop_log: "Logistik", bitop_pur: "Einkauf",
  bitop: "bitop", customer: "Kunde", cmo: "CMO", authority: "Behörde",
  carrier: "Spedition", supplier: "Lieferant", forwarder: "Spedition", qa: "QA",
};
export function akteurLabel(s?: string | null): string | undefined {
  if (!s) return undefined;
  return s.replace(/[a-z][a-z_]*/g, (t) => AKTEUR_MAP[t] ?? t);
}

// ── Kopfband-Bausteine ───────────────────────────────────────────────────────
export function BackButton({ fallback, label }: { fallback: string; label: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onBack = () => {
    // location.key === "default" → Erst-Eintrag (Deep-Link), keine In-App-Historie.
    if (location.key && location.key !== "default") navigate(-1);
    else navigate(fallback);
  };
  return (
    <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-text-2 hover:text-text">
      <ArrowLeft className="w-4 h-4" /> {label}
    </button>
  );
}

// Ein Breadcrumb-Glied: nackter String (nicht klickbar) ODER {label, to} mit
// echter Route → klickbarer Link (Befund 2). Das letzte Glied bleibt immer Text.
export type CrumbPart = string | { label: string; to?: string };
function Crumb({ parts }: { parts: CrumbPart[] }) {
  const norm = parts.map((p) => (typeof p === "string" ? { label: p, to: undefined } : p));
  return (
    <nav aria-label="Breadcrumb" className="text-[0.82rem] text-text-3 mb-3 flex items-center gap-1.5 flex-wrap">
      {norm.map((p, i) => {
        const last = i === norm.length - 1;
        return (
          <span key={i} className="inline-flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3 text-text-3" />}
            {p.to && !last ? (
              <Link to={p.to}
                className="text-brand-600 hover:text-brand-700 hover:underline font-medium rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                {p.label}
              </Link>
            ) : (
              <span className={clsx(last ? "text-text font-medium" : "")}>{p.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

type KpiTone = "brand" | "warning" | "success" | "danger" | "default";
export function KpiChip({ label, value, tone = "default" }: { label: string; value: ReactNode; tone?: KpiTone }) {
  const valCls = tone === "brand" ? "text-brand-500" : tone === "warning" ? "text-warning"
    : tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : "text-text";
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-md border border-border bg-surface-2 px-2.5 py-1">
      <span className="text-[0.64rem] uppercase tracking-wide text-text-3">{label}</span>
      <span className={clsx("ea-num text-[0.86rem] font-semibold tabular-nums", valCls)}>{value}</span>
    </span>
  );
}

export function EntityPage({ backTo, backLabel, crumbs, kicker, title, subtitle, kpis, actions, children }: {
  backTo: string; backLabel: string; crumbs: CrumbPart[];
  kicker?: ReactNode; title: string; subtitle?: ReactNode;
  kpis?: ReactNode; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-3"><BackButton fallback={backTo} label={backLabel} /></div>
      <Crumb parts={crumbs} />
      <header className="mb-5 border-b border-border pb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            {kicker}
            <h1 className="text-2xl font-semibold text-text leading-tight">{title}</h1>
            {subtitle && <div className="mt-1 text-sm text-text-3">{subtitle}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap shrink-0">{actions}</div>}
        </div>
        {kpis && <div className="mt-3 flex items-center gap-2 flex-wrap">{kpis}</div>}
      </header>
      {children}
    </div>
  );
}
